import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from '@/lib/AuthProvider';
import { useToast } from '@/components/ui/use-toast';
import LoadingSpinner from './LoadingSpinner';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const { toast } = useToast();

  const shouldSuppressAuthToast = () => {
    const recentLogoutTs = sessionStorage.getItem('recent_logout');
    if (!recentLogoutTs) return false;

    const elapsed = Date.now() - Number(recentLogoutTs);
    const SUPPRESS_WINDOW_MS = 10_000; // 10 seconds

    return elapsed >= 0 && elapsed < SUPPRESS_WINDOW_MS;
  };

  useEffect(() => {
    // Show toast notification when redirected due to auth requirements
    if (!loading && !user && !shouldSuppressAuthToast()) {
      toast({
        title: "Authentication required",
        description: "Please sign in to access this page",
        variant: "destructive",
      });
    }
  }, [loading, user, toast]);

  // Add SEO meta tags to prevent indexing of protected pages
  const ProtectedPageMeta = () => (
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
      <meta name="googlebot" content="noindex, nofollow" />
      <meta name="bingbot" content="noindex, nofollow" />
    </Helmet>
  );

  // If still loading, show loading spinner
  if (loading) {
    return (
      <>
        <ProtectedPageMeta />
        <LoadingSpinner />
      </>
    );
  }

  // If not authenticated, redirect to home page
  if (!user) {
    return (
      <>
        <ProtectedPageMeta />
        <Navigate to="/" state={{ from: location }} replace />
      </>
    );
  }

  // If authenticated, render the protected content
  return (
    <>
      <ProtectedPageMeta />
      {children}
    </>
  );
};

export default ProtectedRoute; 
